import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setUser } from '../../redux/userSlice';
import { setLoading } from '../../redux/loaderSlice';
import AnimatedInput from '../../components/common/animated-input';
import ValidationError from '../../components/common/validation-error';
import { loginSchema, signupSchema } from '../../validation/schemas';

export default function LoginPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isFlipped, setIsFlipped] = useState(false);

  // Separate states for each form
  const [loginValues, setLoginValues] = useState({
    username: '',
    password: '',
  });
  const [signupValues, setSignupValues] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  // Separate error states
  const [loginErrors, setLoginErrors] = useState({});
  const [signupErrors, setSignupErrors] = useState({});

  // Common validation handler
  const validateForm = async (schema, values, setErrorFn) => {
    try {
      await schema.validate(values, { abortEarly: false });
      setErrorFn({});
      return true;
    } catch (err) {
      const newErrors = {};
      err.inner.forEach((e) => (newErrors[e.path] = e.message));
      setErrorFn(newErrors);
      return false;
    }
  };

  // Login handler
  const handleLogin = async () => {
    const valid = await validateForm(loginSchema, loginValues, setLoginErrors);
    if (!valid) return;

    dispatch(setLoading(true));
    setTimeout(() => {
      dispatch(setUser({ name: 'John Doe', token: 'mock-token' }));
      dispatch(setLoading(false));
      navigate('/dashboard');
    }, 1500);
  };

  // Signup handler
  const handleSignup = async () => {
    const valid = await validateForm(
      signupSchema,
      signupValues,
      setSignupErrors
    );
    if (!valid) return;

    dispatch(setLoading(true));
    setTimeout(() => {
      dispatch(setUser({ name: 'New User', token: 'mock-token' }));
      dispatch(setLoading(false));
      navigate('/dashboard');
    }, 1500);
  };

  return (
    <div className='relative flex items-center justify-center min-h-[calc(100vh-5rem)] bg-black text-white overflow-hidden selection:bg-white selection:text-black'>
      {/* Clean background */}
      <div className='absolute inset-0 bg-black'></div>

      {/* Main Container */}
      <div className='relative w-full max-w-md p-8 z-10'>

        {/* Mode Switcher (Login / Signup) */}
        <div className='flex justify-center mb-12 relative'>
          <div className='relative flex bg-white/5 rounded-full p-1 border border-white/10'>
            <div
              className={`absolute top-1 bottom-1 w-[calc(50%-4px)] bg-white rounded-full transition-all duration-500 ease-[cubic-bezier(0.23,1,0.32,1)] ${isFlipped ? 'left-[calc(50%)]' : 'left-1'
                }`}
            ></div>
            <button
              onClick={() => setIsFlipped(false)}
              className={`relative z-10 px-8 py-2 text-sm font-bold transition-colors duration-300 ${!isFlipped ? 'text-black' : 'text-gray-400 hover:text-white'
                }`}
            >
              Login
            </button>
            <button
              onClick={() => setIsFlipped(true)}
              className={`relative z-10 px-8 py-2 text-sm font-bold transition-colors duration-300 ${isFlipped ? 'text-black' : 'text-gray-400 hover:text-white'
                }`}
            >
              Sign Up
            </button>
          </div>
        </div>

        {/* Forms Container */}
        <div className='relative min-h-[400px]'>
          {/* LOGIN FORM */}
          <div
            className={`absolute inset-0 transition-all duration-500 ease-[cubic-bezier(0.23,1,0.32,1)] ${!isFlipped
              ? 'opacity-100 translate-x-0 pointer-events-auto'
              : 'opacity-0 -translate-x-10 pointer-events-none'
              }`}
          >
            <h2 className='text-4xl font-bold mb-2 tracking-tight'>Welcome Back</h2>
            <p className='text-gray-400 mb-8 text-sm'>Enter your credentials to access your account.</p>

            <div className='space-y-6'>
              <div className='relative'>
                <AnimatedInput
                  label='Username'
                  delayStep={40}
                  value={loginValues.username}
                  onChange={(val) =>
                    setLoginValues({ ...loginValues, username: val })
                  }
                />
                <ValidationError message={loginErrors.username} />
              </div>

              <div className='relative'>
                <AnimatedInput
                  label='Password'
                  delayStep={40}
                  type='password'
                  showToggle
                  value={loginValues.password}
                  onChange={(val) =>
                    setLoginValues({ ...loginValues, password: val })
                  }
                />
                <ValidationError message={loginErrors.password} />
              </div>

              <button
                onClick={handleLogin}
                className='w-full mt-4 bg-white text-black py-4 rounded-lg font-bold tracking-wide
                           hover:scale-[1.02] active:scale-[0.98] transition-all duration-300
                           shadow-[0_0_20px_rgba(255,255,255,0.1)] hover:shadow-[0_0_30px_rgba(255,255,255,0.2)]'
              >
                Login
              </button>
            </div>
          </div>

          {/* SIGNUP FORM */}
          <div
            className={`absolute inset-0 transition-all duration-500 ease-[cubic-bezier(0.23,1,0.32,1)] ${isFlipped
              ? 'opacity-100 translate-x-0 pointer-events-auto'
              : 'opacity-0 translate-x-10 pointer-events-none'
              }`}
          >
            <h2 className='text-4xl font-bold mb-2 tracking-tight'>Create Account</h2>
            <p className='text-gray-400 mb-8 text-sm'>Join us and start your journey today.</p>

            <div className='space-y-5'>
              {[
                { key: 'fullName', label: 'Full Name', type: 'text' },
                { key: 'email', label: 'Email', type: 'email' },
                {
                  key: 'password',
                  label: 'Password',
                  type: 'password',
                  showToggle: true,
                },
                {
                  key: 'confirmPassword',
                  label: 'Confirm Password',
                  type: 'password',
                  showToggle: true,
                },
              ].map(({ key, label, type, showToggle }) => (
                <div key={key} className='relative'>
                  <AnimatedInput
                    label={label}
                    delayStep={40}
                    type={type}
                    showToggle={showToggle}
                    value={signupValues[key]}
                    onChange={(val) =>
                      setSignupValues({ ...signupValues, [key]: val })
                    }
                  />
                  <ValidationError message={signupErrors[key]} />
                </div>
              ))}

              <button
                onClick={handleSignup}
                className='w-full mt-4 bg-white text-black py-4 rounded-lg font-bold tracking-wide
                           hover:scale-[1.02] active:scale-[0.98] transition-all duration-300
                           shadow-[0_0_20px_rgba(255,255,255,0.1)] hover:shadow-[0_0_30px_rgba(255,255,255,0.2)]'
              >
                Sign Up
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
